import { useReducer, useEffect, useRef, useCallback } from 'react';
import type {
  IPAction,
  IPState,
  LikertResponse,
} from '../types/investmentPhilosophy';
import { QUESTIONS, ARCHETYPES } from '../config/investmentArchetypes';
import { buildUserProfile, computeMatches } from '../utils/investmentPhilosophyMatching';

const STORAGE_KEY = 'wallstreetle-investment-philosophy-v4';

const TOTAL_QUESTIONS = QUESTIONS.length;

const initialState: IPState = {
  phase: 'intro',
  currentQuestionIndex: 0,
  responses: Array(TOTAL_QUESTIONS).fill(null) as (LikertResponse | null)[],
  profile: null,
  matches: null,
};

interface PersistedState {
  phase: IPState['phase'];
  currentQuestionIndex: number;
  responses: (LikertResponse | null)[];
  profile: IPState['profile'];
  matches: IPState['matches'];
}

function loadPersisted(): PersistedState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedState;
    if (!Array.isArray(parsed.responses) || parsed.responses.length !== TOTAL_QUESTIONS) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function persist(state: IPState): void {
  try {
    const payload: PersistedState = {
      phase: state.phase,
      currentQuestionIndex: state.currentQuestionIndex,
      responses: state.responses,
      profile: state.profile,
      matches: state.matches,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage may be full or unavailable
  }
}

export function clearInvestmentPhilosophyStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

function reducer(state: IPState, action: IPAction): IPState {
  switch (action.type) {
    case 'START_QUIZ':
      return { ...state, phase: 'active', currentQuestionIndex: 0 };
    case 'ANSWER': {
      const responses = [...state.responses];
      responses[action.index] = action.response;
      const isLast = action.index === TOTAL_QUESTIONS - 1;
      return {
        ...state,
        responses,
        phase: isLast ? 'computing' : 'active',
        currentQuestionIndex: isLast ? action.index : action.index + 1,
      };
    }
    case 'GO_BACK':
      return {
        ...state,
        currentQuestionIndex: Math.max(0, state.currentQuestionIndex - 1),
      };
    case 'COMPUTE_RESULTS':
      return {
        ...state,
        phase: 'results',
        profile: action.profile,
        matches: action.matches,
      };
    case 'RESTART':
      return {
        ...initialState,
        responses: Array(TOTAL_QUESTIONS).fill(null) as (LikertResponse | null)[],
      };
    case 'RESTORE':
      return { ...state, ...action.state };
    default:
      return state;
  }
}

export function useInvestmentPhilosophy() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const hydrated = useRef(false);

  // Hydrate once on mount
  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    const saved = loadPersisted();
    if (saved) {
      dispatch({ type: 'RESTORE', state: saved });
    }
  }, []);

  // Persist on every change (after hydration)
  useEffect(() => {
    if (!hydrated.current) return;
    persist(state);
  }, [state]);

  // Compute results when entering 'computing' phase
  useEffect(() => {
    if (state.phase !== 'computing') return;
    const allAnswered = state.responses.every((r) => r != null);
    if (!allAnswered) return;

    const responses = state.responses as LikertResponse[];
    const timer = setTimeout(() => {
      const profile = buildUserProfile(responses, QUESTIONS);
      const matches = computeMatches(profile, ARCHETYPES);
      dispatch({ type: 'COMPUTE_RESULTS', profile, matches });
    }, 600);

    return () => clearTimeout(timer);
  }, [state.phase, state.responses]);

  const startQuiz = useCallback(() => dispatch({ type: 'START_QUIZ' }), []);
  const answer = useCallback(
    (index: number, response: LikertResponse) =>
      dispatch({ type: 'ANSWER', index, response }),
    []
  );
  const goBack = useCallback(() => dispatch({ type: 'GO_BACK' }), []);
  const restart = useCallback(() => {
    clearInvestmentPhilosophyStorage();
    dispatch({ type: 'RESTART' });
  }, []);

  return {
    state,
    totalQuestions: TOTAL_QUESTIONS,
    startQuiz,
    answer,
    goBack,
    restart,
  };
}
